/**
 * Reenvio do termo aos profissionais do programa (HNS-1594).
 *
 * Espelha a tela de atualização de termos do paciente, mas o profissional assina por
 * outro caminho: ele recebe o link aberto de aceite (/termos-medico/aceite/<uuid>),
 * onde lê o termo e responde as perguntas configuradas para a categoria dele.
 *
 * Só entra quem tem termo de plataforma pendente — a verificação usa a mesma regra que
 * a plataforma aplica ao cobrar o aceite no login, então quem aparece aqui é exatamente
 * quem verá o termo ao abrir o link.
 *
 * O envio vai em lotes (limite do backend) e quem já recebeu hoje não entra de novo, para
 * que pedir o próximo lote não reenvie para os mesmos.
 */
import React, { Component } from "react";
import { Card, Row, Col, Form, Button, Alert, Table, Spinner, Badge } from "react-bootstrap";
import { FaEye, FaPaperPlane, FaExclamationTriangle, FaUserSlash } from "react-icons/fa";
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import api from "../../services/api";

const MySwal = withReactContent(Swal);

const CATEGORIAS = [
    { valor: '', rotulo: 'Todas as categorias' },
    { valor: 'medicos', rotulo: 'Médicos' },
    { valor: 'multiprofissionais', rotulo: 'Multiprofissionais' },
    { valor: 'assessor_tecnico', rotulo: 'Assessor Técnico' },
    { valor: 'laboratorio_diagnostico', rotulo: 'Laboratório Diagnóstico' },
    { valor: 'clinicas', rotulo: 'Clínicas' },
    { valor: 'hospitais', rotulo: 'Hospitais' },
];

const ASSUNTO_PADRAO = 'Aceite do termo — [nome_programa]';
const MENSAGEM_PADRAO =
    '<p>Olá <strong>[nome_prestador]</strong>,</p>'
    + '<p>O termo do programa <strong>[nome_programa]</strong> foi atualizado. '
    + 'Para seguir participando, pedimos que leia e registre o seu aceite.</p>'
    + '<p><a href="[link_aceitetermo]">Clique aqui para aceitar o termo</a></p>';

class AtualizacaoTermosPrestador extends Component {
    constructor(props) {
        super(props);
        this.state = {
            categoria: '',
            prestadores_ids: '',
            email_assunto: ASSUNTO_PADRAO,
            email_mensagem: MENSAGEM_PADRAO,
            incluir_sem_usuario: false,
            previa: null,
            carregando: false,
            enviando: false,
            erro: '',
            ultimoEnvio: null,
        };
    }

    componentDidUpdate(prevProps) {
        if (prevProps.programaId !== this.props.programaId) {
            this.setState({ previa: null, ultimoEnvio: null, erro: '' });
        }
    }

    montarPayload = (preview) => {
        const ids = (this.state.prestadores_ids || '')
            .split(/[^\d]+/).filter(Boolean).join(',');

        return {
            programa_id: this.props.programaId,
            categoria: this.state.categoria || undefined,
            prestadores_ids: ids || undefined,
            incluir_sem_usuario: this.state.incluir_sem_usuario,
            email_assunto: this.state.email_assunto,
            email_mensagem: this.state.email_mensagem,
            preview,
        };
    };

    verPrevia = async () => {
        this.setState({ carregando: true, erro: '', previa: null });

        try {
            const { data } = await api.post('/termos/prestador/atualizacao-termos/', this.montarPayload(true));
            this.setState({ previa: data, carregando: false });
        } catch (e) {
            console.error('Erro ao consultar a prévia do reenvio de termos:', e);
            this.setState({
                carregando: false,
                erro: e?.response?.data?.detail || 'Não foi possível consultar quem seria notificado.',
            });
        }
    };

    confirmarEnvio = async () => {
        const { previa } = this.state;
        const agora = previa?.enviados?.length ?? 0;
        const total = previa?.total_pendentes ?? agora;

        if (!agora) {
            MySwal.fire({
                title: 'Ninguém a notificar',
                html: '<p>Nenhum profissional com termo pendente foi encontrado com os filtros atuais.</p>',
                icon: 'info',
            });
            return;
        }

        const confirma = await MySwal.fire({
            title: 'Enviar o link de aceite?',
            html: `<p>Serão notificados agora <strong>${agora}</strong> profissional(is), `
                + `de um total de <strong>${total}</strong> com termo pendente.</p>`
                + '<p class="text-muted" style="font-size:.9em">Cada um recebe o link aberto de aceite, '
                + 'com as perguntas da categoria dele.</p>',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sim, enviar',
            cancelButtonText: 'Cancelar',
        });

        if (!confirma.isConfirmed) return;

        this.setState({ enviando: true, erro: '' });

        try {
            const { data } = await api.post('/termos/prestador/atualizacao-termos/', this.montarPayload(false));
            const enviados = data.enviados || [];
            const falhas = enviados.filter(e => e.falhou).length;
            const restam = (data.total_pendentes || 0) - enviados.length;

            this.setState({ enviando: false, ultimoEnvio: data, previa: null });

            MySwal.fire({
                title: 'Envio concluído',
                html: `<p><strong>${enviados.length - falhas}</strong> profissional(is) notificado(s).</p>`
                    + (falhas ? `<p class="text-danger">${falhas} envio(s) falharam.</p>` : '')
                    + (restam > 0
                        ? `<p>Ainda faltam <strong>${restam}</strong>. Repita para o próximo lote.</p>`
                        : '<p>Não há mais profissionais pendentes.</p>'),
                icon: falhas ? 'warning' : 'success',
            });
        } catch (e) {
            console.error('Erro ao enviar o reenvio de termos:', e);
            this.setState({
                enviando: false,
                erro: e?.response?.data?.detail || 'Não foi possível enviar.',
            });
        }
    };

    renderLista = (titulo, itens, variante) => {
        if (!itens || !itens.length) return null;

        return (
            <div className="mt-3">
                <h6>{titulo} <Badge bg={variante}>{itens.length}</Badge></h6>
                <div style={{ maxHeight: '15rem', overflowY: 'auto' }}>
                    <Table size="sm" bordered responsive className="mb-0">
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th>E-mail</th>
                                <th>Categoria</th>
                                <th>Canal</th>
                                <th>Termos pendentes</th>
                            </tr>
                        </thead>
                        <tbody>
                            {itens.map((p) => (
                                <tr key={p.id}>
                                    <td>{p.nome}</td>
                                    <td>{p.email}</td>
                                    <td>{p.categoria || '-'}</td>
                                    <td className="text-nowrap">
                                        {/* na prévia mostra por onde sairia; depois do envio, por onde saiu */}
                                        {((p.entregues || p.canais || []).length
                                            ? (p.entregues || p.canais)
                                            : ['—']).map((c) => (
                                            c === '—'
                                                ? <span key="vazio" className="text-muted">—</span>
                                                : <Badge key={c} bg={c === 'whatsapp' ? 'success' : 'light'}
                                                         text={c === 'whatsapp' ? undefined : 'dark'} className="me-1">
                                                      {c === 'whatsapp' ? 'WhatsApp' : 'E-mail'}
                                                  </Badge>
                                        ))}
                                    </td>
                                    <td>
                                        {/* na lista de e-mail inválido o que interessa é o motivo, não o termo */}
                                        {p.motivo && <span className="text-danger">{p.motivo}</span>}
                                        {!p.motivo && (p.termos_pendentes || []).map(t => (
                                            <div key={t.id}>{t.titulo} <small className="text-muted">v{t.versao}</small></div>
                                        ))}
                                        {!p.motivo && !(p.termos_pendentes || []).length && <span className="text-muted">—</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </div>
        );
    };

    render() {
        const { programaId } = this.props;
        const { categoria, prestadores_ids, email_assunto, email_mensagem, incluir_sem_usuario,
                previa, carregando, enviando, erro, ultimoEnvio } = this.state;

        if (!programaId) return null;

        return (
            <Card className="cardDestaque">
                <Card.Body>
                    <h4>Reenvio do termo aos profissionais</h4>
                    <p className="text-muted" style={{ fontSize: '.92em' }}>
                        Envia o link de aceite aos profissionais do programa que ainda não aceitaram a
                        versão vigente do termo da plataforma. Confira antes de enviar — a verificação
                        não dispara nada.
                    </p>

                    {erro && <Alert variant="danger"><FaExclamationTriangle className="me-2" />{erro}</Alert>}

                    <Row>
                        <Col sm={4} xs={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>Categoria</Form.Label>
                                <Form.Select
                                    value={categoria}
                                    onChange={(e) => this.setState({ categoria: e.target.value, previa: null })}
                                >
                                    {CATEGORIAS.map(c => (
                                        <option key={c.valor} value={c.valor}>{c.rotulo}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        <Col sm={4} xs={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>Profissionais específicos <small className="text-muted">(opcional)</small></Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Ex.: 3397, 3646"
                                    value={prestadores_ids}
                                    onChange={(e) => this.setState({ prestadores_ids: e.target.value, previa: null })}
                                />
                                <Form.Text className="text-muted">Ids separados por vírgula. Em branco, vale o programa todo.</Form.Text>
                            </Form.Group>
                        </Col>

                        <Col sm={4} xs={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>Sem usuário na plataforma</Form.Label>
                                <Form.Check
                                    type="switch"
                                    id="incluir-sem-usuario"
                                    label="Incluir no envio"
                                    checked={incluir_sem_usuario}
                                    onChange={(e) => this.setState({ incluir_sem_usuario: e.target.checked, previa: null })}
                                />
                                <Form.Text className="text-muted">
                                    A pendência é verificada do mesmo jeito; eles só ainda não acessaram a plataforma.
                                </Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col sm={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>Assunto do e-mail</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={email_assunto}
                                    onChange={(e) => this.setState({ email_assunto: e.target.value })}
                                />
                            </Form.Group>
                        </Col>

                        <Col sm={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>Conteúdo do e-mail</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={6}
                                    value={email_mensagem}
                                    onChange={(e) => this.setState({ email_mensagem: e.target.value })}
                                />
                                <Form.Text className="text-muted">
                                    Variáveis: <code>[nome_prestador]</code>, <code>[nome_programa]</code> e{' '}
                                    <code>[link_aceitetermo]</code> — o link é montado para cada profissional.
                                </Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>

                    <div className="d-flex gap-2 flex-wrap">
                        <Button variant="outline-secondary" onClick={this.verPrevia} disabled={carregando || enviando}>
                            {carregando ? <Spinner size="sm" animation="border" className="me-2" /> : <FaEye className="me-2" />}
                            Verificar quem receberia
                        </Button>

                        <Button variant="primary" onClick={this.confirmarEnvio} disabled={!previa || enviando || carregando}>
                            {enviando ? <Spinner size="sm" animation="border" className="me-2" /> : <FaPaperPlane className="me-2" />}
                            Enviar
                        </Button>
                    </div>

                    {previa && (
                        <div className="mt-4">
                            <Alert variant={previa.total_pendentes ? 'info' : 'secondary'} className="mb-2">
                                <strong>{previa.total_pendentes}</strong> profissional(is) com termo pendente.{' '}
                                {previa.total_pendentes > (previa.enviados || []).length && (
                                    <>Neste lote sairiam <strong>{(previa.enviados || []).length}</strong>.{' '}</>
                                )}
                                {previa.total_em_dia > 0 && <>{previa.total_em_dia} já está em dia.</>}
                            </Alert>

                            {previa.varredura_truncada && (
                                <Alert variant="warning" className="py-2">
                                    <FaExclamationTriangle className="me-2" />
                                    O programa tem mais profissionais do que o limite analisado por chamada.
                                    Os demais aparecem depois que estes forem processados.
                                </Alert>
                            )}

                            {!previa.template_whatsapp && (
                                <Alert variant="secondary" className="py-2 mb-2">
                                    Este programa não tem template de WhatsApp configurado para o reenvio;
                                    a notificação sai apenas por e-mail.
                                </Alert>
                            )}

                            {this.renderLista('Receberiam agora', previa.enviados, 'primary')}
                            {this.renderLista('Já receberam hoje', previa.ja_enviados_hoje, 'secondary')}

                            {!!(previa.email_invalido || []).length && (
                                <>
                                    <Alert variant="danger" className="mt-3 py-2 mb-0">
                                        <FaExclamationTriangle className="me-2" />
                                        Estes têm e-mail de preenchimento no cadastro (do tipo
                                        <code className="mx-1">sememail@</code> ou <code className="mx-1">email@email.com</code>)
                                        e não recebem nada. Corrigir o cadastro é o caminho —
                                        enviar para esses endereços só gera devolução.
                                    </Alert>
                                    {this.renderLista('E-mail inválido', previa.email_invalido, 'danger')}
                                </>
                            )}

                            {!!(previa.sem_usuario || []).length && (
                                <>
                                    <Alert variant="warning" className="mt-3 py-2 mb-0">
                                        <FaUserSlash className="me-2" />
                                        Estes têm termo pendente e ainda não acessaram a plataforma — o aceite
                                        pelo link vale do mesmo jeito e já gera o comprovante. {incluir_sem_usuario
                                            ? 'Estão incluídos no envio.'
                                            : 'Ligue a opção acima para incluí-los.'}
                                    </Alert>
                                    {this.renderLista('Sem usuário', previa.sem_usuario, 'warning')}
                                </>
                            )}
                        </div>
                    )}

                    {ultimoEnvio && (
                        <Alert variant="success" className="mt-4 mb-0">
                            Último envio: <strong>{(ultimoEnvio.enviados || []).length}</strong> profissional(is).
                        </Alert>
                    )}
                </Card.Body>
            </Card>
        );
    }
}

export default AtualizacaoTermosPrestador;
